<script lang="ts">
  import SingleBubbleIcon from '$icons/messages/SingleBubbleIcon.svelte';
  import TextArea from '../../TextArea.svelte';
  import Button from '../../Button.svelte';
  import { client } from '$lib/api/Client';
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { projectCommentsQueryKey } from '$lib/pages/Projects/HackathonProjectComments.svelte';
  import { toastCustom } from '$utils/toasts';
  import { ClientResponseError } from '$lib/api/ClientResponseError';
  import type { ProjectComment } from '$shared/lib';
  import type { User } from 'src/routes/(app)/+layout.server';
  import type { ListProjectCommentsResponse } from '$shared/api';
  import { generateTextFromTipTapContent, type TiptapContent } from '$shared/util';
  import RichTextArea from '../../RichTextArea.svelte';
  import { browser } from '$app/environment';
  import type { Editor } from '@tiptap/core';

  export let showReplyInput = false;
  export let parentComment: ProjectComment;
  export let currentUser: User | null;

  let editor: Editor;
  let body: TiptapContent = {
    type: 'doc',
    content: [],
  };

  const queryClient = useQueryClient();
  const createReplyMutation = createMutation({
    mutationFn: async () => {
      return client.projects.createComment({
        projectId: parentComment.projectId,
        parentCommentId: parentComment.id,
        body: body,
      });
    },
    onMutate: async () => {
      if (!currentUser) return;

      await queryClient.cancelQueries({
        queryKey: projectCommentsQueryKey(parentComment.projectId),
      });

      const previousData = queryClient.getQueryData<{
        pages: ListProjectCommentsResponse[];
      }>(projectCommentsQueryKey(parentComment.projectId));

      if (previousData) {
        const updateCommentInTree = (comments: ProjectComment[]): ProjectComment[] => {
          return comments.map((c) => {
            if (c.id === parentComment.id) {
              // Create optimistic reply
              const newReply: ProjectComment = {
                id: Math.floor(Math.random() * 1000000),
                projectId: parentComment.projectId,
                body: body,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                isDeleted: false,
                likedByCurrentUser: false,
                likeCount: 0,
                replyCount: 0,
                hasMoreReplies: false,
                user: {
                  id: currentUser.userId ?? 0,
                  username: currentUser.username ?? '',
                  displayName: currentUser.displayName ?? '',
                  avatarUrl: currentUser.avatarUrl ?? '',
                  accountRoles: [],
                  batches: [],
                },
                replies: [],
              };

              return {
                ...c,
                replyCount: c.replyCount + 1,
                replies: [newReply, ...c.replies],
              };
            }
            if (c.replies?.length) {
              return { ...c, replies: updateCommentInTree(c.replies) };
            }
            return c;
          });
        };

        queryClient.setQueryData(projectCommentsQueryKey(parentComment.projectId), {
          ...previousData,
          pages: previousData.pages.map((page) => ({
            ...page,
            comments: updateCommentInTree(page.comments),
          })),
        });
      }

      return { previousData };
    },
    onError: (error, _, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          projectCommentsQueryKey(parentComment.projectId),
          context.previousData,
        );
      }

      if (error instanceof ClientResponseError) {
        toastCustom({
          title: 'Error',
          description: error.message,
          negative: true,
          position: 'bottom-center',
        });
      } else {
        toastCustom({
          title: 'Error',
          description: 'An unknown error occurred while creating the reply.',
          negative: true,
          position: 'bottom-center',
        });
      }
    },
    onSuccess: async () => {
      // Get the current data
      const currentData = queryClient.getQueryData<{
        pages: ListProjectCommentsResponse[];
      }>(projectCommentsQueryKey(parentComment.projectId));

      if (currentData) {
        // Get just the first page of comments to get the new reply
        const firstPage = await client.projects.listComments({
          projectId: parentComment.projectId,
          parentCommentId: parentComment.id,
          offset: 0,
        });

        // Merge the new reply with existing data
        const updateCommentInTree = (comments: ProjectComment[]): ProjectComment[] => {
          return comments.map((c) => {
            if (c.id === parentComment.id) {
              // Get the new reply (first comment in the response)
              const newReply = firstPage.comments[0];
              // Keep all existing replies except the optimistic one
              const existingReplies = c.replies.slice(1);

              return {
                ...c,
                replies: [newReply, ...existingReplies],
              };
            }
            if (c.replies?.length) {
              return { ...c, replies: updateCommentInTree(c.replies) };
            }
            return c;
          });
        };

        queryClient.setQueryData(projectCommentsQueryKey(parentComment.projectId), {
          ...currentData,
          pages: currentData.pages.map((page) => ({
            ...page,
            comments: updateCommentInTree(page.comments),
          })),
        });
      }
    },
  });

  async function createCommentReply() {
    await $createReplyMutation.mutateAsync();
    body = {
      type: 'doc',
      content: [],
    };
    showReplyInput = false;
  }

  $: isBodyEmpty = generateTextFromTipTapContent(body).length === 0;
</script>

<div class="bg-gray-dark-1 flex flex-col gap-y-4 rounded-md p-4">
  <div class="flex items-center gap-x-2">
    <SingleBubbleIcon class="text-gray-dark-11" />
    <h2 class="text-base">Add a reply</h2>
  </div>
  {#if browser}
    <RichTextArea allowCodeBlock={false} bind:value={body} bind:editor />
  {/if}
  <div class="flex items-center gap-x-4">
    <Button
      tertiary
      on:click={() => {
        showReplyInput = false;
      }}
      disabled={$createReplyMutation.isPending}
    >
      Cancel
    </Button>
    <Button
      secondary
      disabled={isBodyEmpty || $createReplyMutation.isPending}
      on:click={createCommentReply}
      loading={$createReplyMutation.isPending}
    >
      Reply
    </Button>
  </div>
</div>
